import React, { useState, useEffect } from "react";
import {
  Link as RouterLink,
  useParams,
  useNavigate,
  Link,
} from "react-router-dom";
import * as formik from "formik";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { Col } from "react-bootstrap";
import Swal from "sweetalert2";
import axiosClient from "../../../config/axios";
import { orderValidationSchema } from "./orderVialFieldsYupValidation";
import { useAuth } from "../../../context/AuthContext";
import moment from "moment-timezone";

// Componentes del formulario
import { Panel, PanelHeader, PanelBody } from "../../../components/panel/panel";
import InfringementData from "./formOrderVial/InfringementData";
import OrderVialVehiclesForm from "./formOrderVial/orderVialVehiclesForm";
import ServiceDetails from "./formOrderVial/serviceDetails";
import VehicleEquipmentDetails from "./formOrderVial/vehicleEquipmentDetails";
import CraneData from "./formOrderVial/craneData";
import PaymentData from "./formOrderVial/paymentData";
import Loader from "../../../components/loader/index.js";
import { useCommon } from "../../../context/CommonContext.js";
import AccordionPayments from "./formOrderVial/accordionPayments.js";
import { useRef } from "react";
import Button from "react-bootstrap/Button";
import AddInvoiceForm from "./formOrderVial/invoice/addInvoiceForm.js"; 
import ModalContainer from "../../../components/ModalContainer.js"; 
import { focusFirstError } from "./formOrderVial/focusFirstError.js";

function formatNumberWithCommas(number) {
  if (number) {
    // const roundedNumber = parseFloat(number);
    const roundedNumber = parseFloat(number).toFixed(2);
    return roundedNumber.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }
  return "";
}

// Clave en localStorage para recordar la última impresora elegida por el usuario.
const LAST_PRINTER_LS_KEY = "lastPrinterDevice";

// Consulta al backend la lista de api-print conectados en este momento.
// El backend devuelve { devices: [{ id, nombre, conectadoEn }] }.
async function fetchConnectedPrinters() {
  try {
    const { data } = await axiosClient.get("/printer-devices");
    const list = Array.isArray(data?.devices) ? data.devices : [];
    return list.filter((d) => d && d.id);
  } catch (err) {
    console.error("Error al consultar impresoras conectadas:", err);
    return [];
  }
}

// Escape básico para inyectar texto en HTML del modal sin romper atributos.
function escapeHtml(str) {
  return String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

// Abre un modal SweetAlert con la lista en vivo de impresoras conectadas,
// mostrando una "tarjeta-botón" grande por cada impresora. Al hacer click en
// una tarjeta se elige esa impresora y se cierra el modal inmediatamente.
// Devuelve el id seleccionado, o null si canceló / no hay impresoras.
async function askPrinterDestination() {
  // Loader breve mientras consulta al backend.
  Swal.fire({
    title: "Buscando impresoras...",
    allowOutsideClick: false,
    allowEscapeKey: false,
    didOpen: () => Swal.showLoading(),
  });

  const printers = await fetchConnectedPrinters();
  Swal.close();

  if (printers.length === 0) {
    await Swal.fire(
      "Sin impresoras conectadas",
      "No hay ninguna impresora (api-print) conectada en este momento. Verifica que esté encendida y conectada al servidor.",
      "warning"
    );
    return null;
  }

  const lastUsed = localStorage.getItem(LAST_PRINTER_LS_KEY);

  // Construye una tarjeta-botón por cada impresora conectada. Marcamos la
  // última usada con una pequeña etiqueta visual para que el usuario la
  // identifique rápido.
  const cardsHtml = printers
    .map((p) => {
      const nombre = (p.nombre || "").trim() || p.id;
      const isLast = p.id === lastUsed;
      return `
        <button type="button" class="printer-card-btn" data-id="${escapeHtml(p.id)}">
          ${isLast ? `<span class="printer-card-badge">Última usada</span>` : ""}
          <div class="printer-card-icon"><i class="fa fa-desktop"></i></div>
          <div class="printer-card-label">
            <span class="printer-card-label-action">Imprimir</span>
            <span class="printer-card-label-name">${escapeHtml(nombre)}</span>
          </div>
        </button>
      `;
    })
    .join("");

  // Si solo hay 1 impresora la mostramos a ancho completo; con 2 o más usamos
  // un grid de 2 columnas para que se vean parejas y grandes.
  const gridClass =
    printers.length === 1 ? "printer-cards-grid one" : "printer-cards-grid two";

  let selectedId = null;

  await Swal.fire({
    title: "Seleccionar impresora",
    html: `
      <style>
        .printer-cards-help {
          color: #6c757d;
          margin: 0 0 18px 0;
          font-size: 0.95rem;
        }
        .printer-cards-grid {
          display: grid;
          gap: 16px;
          margin-bottom: 8px;
        }
        .printer-cards-grid.one { grid-template-columns: 1fr; }
        .printer-cards-grid.two { grid-template-columns: 1fr 1fr; }
        @media (max-width: 480px) {
          .printer-cards-grid.two { grid-template-columns: 1fr; }
        }
        .printer-card-btn {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 22px 14px;
          background: #ffffff;
          border: 2px solid #e3e6ef;
          border-radius: 14px;
          cursor: pointer;
          transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease, background 0.12s ease;
          color: #2d3a4b;
          min-height: 150px;
          font-family: inherit;
        }
        .printer-card-btn:hover {
          border-color: #6c5ce7;
          background: #f5f3ff;
          box-shadow: 0 6px 18px rgba(108, 92, 231, 0.18);
          transform: translateY(-2px);
        }
        .printer-card-btn:active {
          transform: translateY(0);
          box-shadow: 0 2px 6px rgba(108, 92, 231, 0.18);
        }
        .printer-card-icon {
          width: 58px;
          height: 58px;
          border-radius: 50%;
          background: #eef0fb;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          color: #6c5ce7;
        }
        .printer-card-btn:hover .printer-card-icon {
          background: #6c5ce7;
          color: #ffffff;
        }
        .printer-card-label {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2px;
          line-height: 1.2;
        }
        .printer-card-label-action {
          font-size: 0.85rem;
          color: #6c757d;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .printer-card-label-name {
          font-size: 1.05rem;
          font-weight: 600;
          color: #2d3a4b;
          word-break: break-word;
        }
        .printer-card-badge {
          position: absolute;
          top: 8px;
          right: 8px;
          background: #6c5ce7;
          color: #ffffff;
          font-size: 0.7rem;
          padding: 2px 8px;
          border-radius: 999px;
          font-weight: 600;
          letter-spacing: 0.3px;
        }
      </style>
      <p class="printer-cards-help">¿A qué impresora quieres mandar el ticket?</p>
      <div class="${gridClass}">${cardsHtml}</div>
    `,
    showConfirmButton: false,
    showCancelButton: true,
    cancelButtonText: "Cancelar",
    focusCancel: false,
    didOpen: () => {
      const popup = Swal.getPopup();
      if (!popup) return;
      popup.querySelectorAll(".printer-card-btn").forEach((btn) => {
        btn.addEventListener("click", () => {
          selectedId = btn.getAttribute("data-id");
          Swal.close();
        });
      });
    },
  });

  if (selectedId) {
    try {
      localStorage.setItem(LAST_PRINTER_LS_KEY, selectedId);
    } catch (e) {
      /* localStorage puede fallar en modo privado, no es crítico */
    }
    return selectedId;
  }
  return null;
}

function EditOrderVial() {
  const { id } = useParams(); // ID de la orden
  const [initialValues, setInitialValues] = useState(null);
  const { Formik } = formik;
  const navigate = useNavigate();
  const { token, userId, fullName, username, roles, isAdmin, isAccountant } =
    useAuth();

  const { loading, setLoading } = useCommon();
  const [paymentTypes, setPaymentTypes] = useState([]);
  const tablePaymentsContadoRef = useRef();
  const [pagosModificados, setPagosModificados] = useState(false);

  const [openModalInvoice, setOpenModalInvoice] = useState(false);
  const openInvoiceModal = () => {
    setOpenModalInvoice(true);
  };

  const closeInvoiceModal = () => {
    setOpenModalInvoice(false);
  };

  // Invalida la tabla de facturas selladas al guardar una nueva (AccordionPayments / datatable).
  const [refreshKey, setRefreshKey] = useState(0);
  const handleInvoiceSaved = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const [isCancelling, setIsCancelling] = useState(false);
  const { canCancelVaroExpress } = useAuth();
  const [shouldPrintTicket, setShouldPrintTicket] = useState(false);
  const [shouldrepRintTicket, setShouldReprintTicket] = useState(false);
  // Guarda el id de la impresora elegida ANTES de disparar el submit, para que
  // el modal aparezca al hacer click (sin spinner encima) y no durante el submit.
  const printerDestinoRef = useRef(null);

  function isPaymentCovered(values) {
    const paymentsContado = values.payments_contado || [];
    const paymentsCredito = values.payments_credito || [];
    const costosAdicionales = values.montosExtras.reduce((acc, item) => {
      const monto = parseFloat(item.monto_extra_monto);
      return acc + (isNaN(monto) ? 0 : monto);
    }, 0);

    const total = parseFloat(values.subtotal || 0) + costosAdicionales;

    const sumContado = paymentsContado.reduce(
      (acc, p) => acc + parseFloat(p.payment_amount || 0),
      0
    );
    const sumCredito = paymentsCredito.reduce(
      (acc, p) => acc + parseFloat(p.payment_amount || 0),
      0
    );

    return sumContado + sumCredito >= total;
  }

  useEffect(() => {
    axiosClient
      .get("/paymentTypes/all")
      .then((response) => {
        const formattedOptions = response.data.data.map((paymentType) => ({
          value: paymentType.id,
          label: paymentType.payment_type_name,
          code: paymentType.payment_type_code,
          id: paymentType.id,
        }));
        setPaymentTypes(formattedOptions);
      })
      .catch((error) => {
        console.error("Error fetching paymentTypes:", error);
      });
  }, []);

  const formatDateTimeLocal = (dateString) => {
    if (!dateString) return "";
    return moment.utc(dateString).format("YYYY-MM-DDTHH:mm");
  };

  useEffect(() => {
    setLoading(true);
    axiosClient
      .get(`/order-vial/${id}`)
      .then(({ data }) => {
        const order = data;
        const vehiculoNumero = order.vehiculoNumero || 1;

        // Transformar vehicles a vehiculos
        // Si no hay vehículos, crear un objeto vacío para poder agregar uno
        let vehiculos = [];
        if (order.vehicles && order.vehicles.length > 0) {
          vehiculos = order.vehicles.map((vehiculo, index) => ({
            id: vehiculo.id, // Incluir el ID para poder actualizarlo
            vehiculoNumero: vehiculoNumero,
            placas: vehiculo.placas || "",

            anio: vehiculo.anio
              ? {
                  label: vehiculo.anio.toString(),
                  value: vehiculo.anio,
                }
              : null,

            vehicle_type_id: vehiculo.VehicleModel?.VehicleType
              ? {
                  label: vehiculo.VehicleModel.VehicleType.vehicle_type_name,
                  value: vehiculo.VehicleModel.VehicleType.id,
                }
              : null,

            brand_id: vehiculo.VehicleModel?.Brand
              ? {
                  label: vehiculo.VehicleModel.Brand.brand_name,
                  value: vehiculo.VehicleModel.Brand.id,
                }
              : null,

            vehicle_model_id: vehiculo.VehicleModel
              ? {
                  label: vehiculo.VehicleModel.model_name,
                  value: vehiculo.VehicleModel.id,
                }
              : null,

            color_id: vehiculo.Color
              ? {
                  label: vehiculo.Color.color_name,
                  value: vehiculo.Color.id,
                  color: vehiculo.Color.color_code,
                  colorContrast: vehiculo.Color.color_contrast,
                }
              : null,

            shielding_type_id: vehiculo.ShieldingType
              ? {
                  label: vehiculo.ShieldingType.shielding_type,
                  value: vehiculo.ShieldingType.id,
                }
              : null,

            numero_serie: vehiculo.numero_serie || "",
            observaciones: vehiculo.observaciones || "",
            registrar_volumen: vehiculo.registrar_volumen || false,
            carga_kg: vehiculo.carga_kg || "",
            volumen_largo: vehiculo.volumen_largo || null,
            volumen_ancho: vehiculo.volumen_ancho || null,
            volumen_altura: vehiculo.volumen_altura || null,
            volumen_m3: vehiculo.volumen_m3 || null,
            estado_llantas_giran: vehiculo.estado_llantas_giran ?? null,
            estado_volante_gira: vehiculo.estado_volante_gira ?? null,
            estado_pie_calle: vehiculo.estado_pie_calle ?? null,
            estado_neutral: vehiculo.estado_neutral ?? null,
            estado_en_cajon: vehiculo.estado_en_cajon ?? null,
            asistencia_paso_corriente: vehiculo.asistencia_paso_corriente ?? null,
            asistencia_combustible: vehiculo.asistencia_combustible ?? null,
            asistencia_cambio_neumatico:
              vehiculo.asistencia_cambio_neumatico ?? null,
            asistencia_cerrajeria: vehiculo.asistencia_cerrajeria ?? null,
            // Aquí puedes mapear más campos según lo necesites
          }));
        } else {
          // Si no hay vehículos, crear un objeto vacío para poder agregar uno
          vehiculos = [
            {
              vehiculoNumero: vehiculoNumero || "",
              vehicle_type_id: null,
              brand_id: null,
              vehicle_model_id: null,
              anio: null,
              color_id: null,
              observaciones: "",
              placas: "",
              numero_serie: "",
              shielding_type_id: null,
              carga_kg: "",
              registrar_volumen: false,
              volumen_largo: null,
              volumen_ancho: null,
              volumen_altura: null,
              volumen_m3: null,
              estado_llantas_giran: null,
              estado_volante_gira: null,
              estado_pie_calle: null,
              estado_neutral: null,
              estado_en_cajon: null,
              asistencia_paso_corriente: null,
              asistencia_combustible: null,
              asistencia_cambio_neumatico: null,
              asistencia_cerrajeria: null,
            },
          ];
        }

        // Asigna los valores precargados

        setInitialValues({
          ...order,
          vehiculos,

          crane_id: order.Crane
            ? {
                value: order.Crane.id,
                label: `#${order.Crane.crane_number}, ${order.Crane.description}`,
              }
            : null,

          operator_id: order.operator
            ? {
                value: order.operator.id,
                label: `${order.operator.name} ${order.operator.first_surname} ${order.operator.second_surname}`,
              }
            : null,

          chofer_auxiliar_id: order.chofer_auxiliar
            ? {
                value: order.chofer_auxiliar.id,
                label: `${order.chofer_auxiliar.name} ${order.chofer_auxiliar.first_surname} ${order.chofer_auxiliar.second_surname}`,
              }
            : null,

          estatus: order.estatus || "Pendiente",
          subtotal: order.subtotal || "",
          montosExtras: order.montosExtras || [],
          notas: order.notas || "",
          extinguidor: !!order.extinguidor,
          llanta_refaccion: !!order.llanta_refaccion,
          gato: !!order.gato,
          herramientas: !!order.herramientas,
          cable_pasa_corriente: !!order.cable_pasa_corriente,
          senalamientos: !!order.senalamientos,
          otros: !!order.otros,
          otros_observaciones: order.otros_observaciones || "",
          fecha_hora_orden: formatDateTimeLocal(order.fecha_hora_orden),
          genera_costo_por_estadia: !!order.genera_costo_por_estadia,
        });
      })
      .catch((error) => {
        console.error("Error al obtener la orden:", error);
        Swal.fire("Error", "No se pudo cargar la orden", "error");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  if (loading || !initialValues) return <Loader isLoading={true} />;

  const tienePagosValidos = (contado = [], credito = [], total = 0) => {
    const todos = [...contado, ...credito];

    if (todos.length === 0) return false;

    const pagosCompletos = todos.every((pago) => {
      const {
        payment_date,
        payment_method_code,
        metodoPagoSAT,
        payment_amount,
        bank_account_id,
      } = pago;

      const isTransfer = payment_method_code === "TRANSFER";

      return (
        payment_date &&
        payment_method_code &&
        metodoPagoSAT &&
        payment_amount &&
        (!isTransfer || bank_account_id) // si es TRANSFER, debe tener cuenta
      );
    });

    // Validar que al menos un pago cubre el total de la orden
    const alMenosUnoCoincideConTotal = todos.some(
      (p) => Number(p.payment_amount) >= Number(total)
    );

    return pagosCompletos && alMenosUnoCoincideConTotal;
  };

  const pagosSonIguales = (pagosActuales, pagosOriginales) => {
    if (pagosActuales.length !== pagosOriginales.length) return false;

    // Ordenamos ambos arrays por ID para asegurar el mismo orden
    const ordenadosActuales = [...pagosActuales].sort((a, b) => a.id - b.id);
    const ordenadosOriginales = [...pagosOriginales].sort(
      (a, b) => a.id - b.id
    );
    //console.log("actuales",ordenadosActuales);
    //console.log("originales",ordenadosOriginales);

    // Comparamos el JSON serializado
    return (
      JSON.stringify(ordenadosActuales) === JSON.stringify(ordenadosOriginales)
    );
  };


  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Inicio</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/orders-vial">Varo Express</Link>
            </li>
            <li className="breadcrumb-item active">Editar orden</li>
          </ol>
          <h1 className="page-header mb-0">Editar Orden - Varo Express</h1>
        </div>
      </div>
      <Formik
        validationSchema={orderValidationSchema}
        initialValues={initialValues}
        enableReinitialize
        validateOnChange
        onSubmit={(values, { setSubmitting, setFieldValue }) => {
          setSubmitting(true);

          const paymentsContado = values.payments_contado || [];
          const paymentsCredito = values.payments_credito || [];
          const invoicesContado = values.invoices_contado || [];
          const invoicesCredito = values.invoices_credito || [];

          const costosAdicionales = values.montosExtras.reduce((acc, item) => {
            const monto = parseFloat(item.monto_extra_monto);
            return acc + (isNaN(monto) ? 0 : monto);
          }, 0);

          const total = parseFloat(values.subtotal || 0) + costosAdicionales;

          // Suma de pagos
          const sumContado = paymentsContado.reduce(
            (acc, p) => acc + parseFloat(p.payment_amount || 0),
            0
          );
          const sumCredito = paymentsCredito.reduce(
            (acc, p) => acc + parseFloat(p.payment_amount || 0),
            0
          );

          // Verifica si alguno coincide con el total
          // const estatus =
          //   sumContado >= total ||
          //   sumCredito >= total ||
          //   sumContado + sumCredito >= total
          //     ? "Pagado"
          //     : values.estatus; // mantiene el estatus actual si no cumple
          // //: "Pendiente";

          //Si el estatus está en cancelado NO se cambia a pagado o a pendiente si se cubre el monto
          //Si el estatus NO esta Cancelado y se cubre el monto entonces se pasa a Pagado
          //Si el estatus NO esta Cancelado y NO se cubre el monto entonces se pasa a Pendiente
          // Verifica si alguno coincide con el total y si no está Cancelado
          const estatus =
            values.estatus === "Cancelado"
              ? "Cancelado"
              : sumContado >= total ||
                sumCredito >= total ||
                sumContado + sumCredito >= total
              ? "Pagado"
              : "Pendiente";

          // Actualiza el formik state
          //setFieldValue("estatus", estatus);
          //console.log("values:", values);
          
          const formattedValues = {
            ...values,
            crane_id: values.crane_id?.value || null,
            operator_id: values.operator_id?.value || null,
            chofer_auxiliar_id: values.chofer_auxiliar_id?.value || null,

            vehicles: values.vehiculos.map((v) => ({
              id: v.id || undefined, // Incluir ID si existe para actualizar, si no existe será undefined y se creará uno nuevo
              vehicle_model_id:
                v.vehicle_model_id?.value || v.vehicle_model_id || null,
              color_id: v.color_id?.value || v.color_id || null,
              shielding_type_id:
                v.shielding_type_id?.value || v.shielding_type_id || null,
              anio: v.anio?.value || v.anio || null,
              placas: v.placas || "",
              numero_serie: v.numero_serie || "",
              observaciones: v.observaciones || "",
              carga_kg: v.carga_kg || null,
              registrar_volumen: v.registrar_volumen || false,
              volumen_largo: v.volumen_largo || null,
              volumen_ancho: v.volumen_ancho || null,
              volumen_altura: v.volumen_altura || null,
              volumen_m3: v.volumen_m3 || null,
              estado_llantas_giran: v.estado_llantas_giran ?? null,
              estado_volante_gira: v.estado_volante_gira ?? null,
              estado_pie_calle: v.estado_pie_calle ?? null,
              estado_neutral: v.estado_neutral ?? null,
              estado_en_cajon: v.estado_en_cajon ?? null,
              asistencia_paso_corriente: v.asistencia_paso_corriente ?? null,
              asistencia_combustible: v.asistencia_combustible ?? null,
              asistencia_cambio_neumatico: v.asistencia_cambio_neumatico ?? null,
              asistencia_cerrajeria: v.asistencia_cerrajeria ?? null,
            })),
            vehiculoNumero: values.vehiculos[0]?.vehiculoNumero
              ? parseInt(values.vehiculos[0].vehiculoNumero, 10)
              : null,
            costosAdicionales: parseFloat(costosAdicionales.toFixed(2)),
            total: parseFloat(total.toFixed(2)),
            subtotal: 0,
            grua: true,
            estatus,
          };

          const invoicesPayload = {
            invoices: [
              ...invoicesContado.map((inv) => ({
                ...inv,
                invoice_amount: parseFloat(inv.invoice_amount),
              })),
              ...invoicesCredito.map((inv) => ({
                ...inv,
                invoice_amount: parseFloat(inv.invoice_amount),
              })),
            ],
          };

          const paymentsPayload = {
            payments: [
              ...paymentsContado.map((inv) => ({
                ...inv,
                payment_amount: parseFloat(inv.payment_amount),
              })),
              ...paymentsCredito.map((inv) => ({
                ...inv,
                payment_amount: parseFloat(inv.payment_amount),
              })),
            ],
          };

          //console.log("Valores del formulario a enviar:xd", formattedValues);
          console.log("Valores del formulario a enviar pagos: ", paymentsPayload);

          axiosClient
            .put(`/order-vial/${id}`, formattedValues)
            .then(() => {
              return axiosClient.post(
                `/invoicesOrderVial/saveUpdateMultiple/${id}`,
                invoicesPayload
              );
            })
            .then(() => {
              return axiosClient.post(
                `/paymentsOrderVial/saveUpdateMultiple/${id}`,
                paymentsPayload
              );
            })
            .then(() => {
              setFieldValue("estatus", estatus);
              Swal.fire("Éxito", "La orden fue actualizada", "success");
              //navigate("/orders-vial");
              setFieldValue('payments_contado_original', JSON.parse(JSON.stringify(values.payments_contado)));
              setFieldValue('payments_credito_original', JSON.parse(JSON.stringify(values.payments_credito)));
            })
            .then(async () => {
              if (shouldPrintTicket) {
                // El destino se eligió en el onClick del botón (antes del
                // submit) y se guardó en este ref. Si no hay destino, no se
                // imprime (caso defensivo: alguien disparó submit sin pasar
                // por el modal).
                const dispositivo = printerDestinoRef.current;
                if (!dispositivo) {
                  setShouldReprintTicket(false);
                  return;
                }

                const ticketPayloadBase = {
                  orderVialId: id,
                  datos: formattedValues,
                  pagos: paymentsPayload.payments,
                  facturas: invoicesPayload.invoices,
                  reimprimir: shouldrepRintTicket,
                  dispositivo,
                };

                // Enviar para CLIENTE
                const ticketPayloadCliente = {
                  ...ticketPayloadBase,
                  tipo_ticket: "CLIENTE",
                };

                // Enviar para OFICINA
                const ticketPayloadOficina = {
                  ...ticketPayloadBase,
                  tipo_ticket: "OFICINA",
                };

                try {
                  await axiosClient.post("/order-vial/emitir-ticket", {
                    payload: ticketPayloadCliente,
                  });

                  await axiosClient.post("/order-vial/emitir-ticket", {
                    payload: ticketPayloadOficina,
                  });

                  const mensaje = shouldrepRintTicket
                    ? "Reimprimiendo tickets"
                    : "Imprimiendo tickets";

                  Swal.fire("Éxito", mensaje, "success");

                  setShouldReprintTicket(false);
                } catch (err) {
                  console.error("Error al emitir los tickets:", err);
                  Swal.fire(
                    "Advertencia",
                    "La orden se guardó, pero no se pudieron imprimir los tickets.",
                    "warning"
                  );
                } finally {
                  printerDestinoRef.current = null;
                }
              }
            })
            .catch((err) => {
              console.error("Error al actualizar pagos:", err);
              const errorMessage =
                err.response?.data?.error || "Ha ocurrido un error inesperado";

              Swal.fire({
                title: "Ups!",
                text: errorMessage,
                icon: "error",
                confirmButtonText: "Cerrar",
                cancelButtonText: "Cancelar",
                showCancelButton: false,
              });
              setSubmitting(false);
            })
            .finally(() => {
              setSubmitting(false);
            })
        }}
      >
        {({
          handleSubmit,
          handleChange,
          handleBlur,
          values,
          touched,
          errors,
          setFieldValue,
          setFieldTouched,
          isSubmitting,
          validateForm,
         }) => {
          const isDisabled =
            (values.estatus === "Pagado" || values.estatus === "Cancelado") && !isAdmin();

          const isDisabledReimprimir = (values.estatus === "Pagado");

         const handleValidatedSubmit = async (e) => {
          e.preventDefault();

          // Calcular total para validación previa
          const costosAdicionales = values.montosExtras.reduce((acc, item) => {
            const monto = parseFloat(item.monto_extra_monto || 0);
            return acc + (isNaN(monto) ? 0 : monto);
          }, 0);
          const totalCalculado = parseFloat(values.subtotal || 0) + costosAdicionales;

          // Validar que el total sea mayor a 0
          if (totalCalculado <= 0) {
            Swal.fire({
              title: "Error de validación",
              text: "El total debe ser mayor a 0.",
              icon: "error",
              confirmButtonText: "Entendido",
            });
            return;
          }

          // Función para limpiar errores
          const normalizeErrors = (obj) => {
            if (typeof obj === "string") return obj;
            if (Array.isArray(obj)) {
              return obj.map((item) => {
                const normalized = normalizeErrors(item);
                // Si el resultado es un objeto con strings, toma el primero
                if (typeof normalized === "object") return Object.values(normalized)[0] || "Campo requerido";
                return normalized;
              });
            }
            if (typeof obj === "object" && obj !== null) {
              const newObj = {};
              for (const key in obj) {
                const val = obj[key];
                newObj[key] = normalizeErrors(val);
              }
              return newObj;
            }
            return "Campo requerido";
          };

          // Validamos el formulario
          let formErrors = await validateForm();

          // Normalizamos los errores
          formErrors = normalizeErrors(formErrors);

          // Si hay errores, enfocamos el primero
          if (Object.keys(formErrors).length > 0) {
            focusFirstError(formErrors, values);
            return;
          }

          // Si no hay errores, procedemos al submit
          handleSubmit(e);
        };


          return (
          <Form noValidate onSubmit={handleValidatedSubmit}>
            <Loader isLoading={isSubmitting} />

            <Row className="mb-3">
              <Col md="12">
                <Panel>
                  <PanelHeader noButton>Datos de la orden</PanelHeader>
                  <PanelBody>
                    <InfringementData
                      values={values}
                      setFieldValue={setFieldValue}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      touched={touched}
                      errors={errors}
                      disabled={isDisabled}
                    />
                  </PanelBody>
                </Panel>

                <Panel>
                  <PanelHeader noButton>Datos del vehículo</PanelHeader>
                  <PanelBody>
                    <OrderVialVehiclesForm
                      token={token}
                      values={values}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      touched={touched}
                      errors={errors}
                      disabled={isDisabled}
                    />
                  </PanelBody>
                </Panel>

                <Panel>
                  <PanelHeader noButton>Datos del servicio</PanelHeader>
                  <PanelBody>
                    <ServiceDetails
                      values={values}
                      setFieldValue={setFieldValue}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      touched={touched}
                      errors={errors}
                      disabled={isDisabled}
                    />
                  </PanelBody>
                </Panel>

                <Panel>
                  <PanelHeader noButton>Detalles del equipamiento</PanelHeader>
                  <PanelBody>
                    <VehicleEquipmentDetails
                      values={values}
                      setFieldValue={setFieldValue}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      touched={touched}
                      errors={errors}
                      disabled={isDisabled}
                    />
                  </PanelBody>
                </Panel>

                <Panel>
                  <PanelHeader noButton>Datos de operación</PanelHeader>
                  <PanelBody>
                    <CraneData
                      values={values}
                      errors={errors}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      disabled={isDisabled}
                    />
                  </PanelBody>
                </Panel>

                <Panel>
                  <PanelHeader noButton>Datos de total a pagar</PanelHeader>
                  <PanelBody>
                    <PaymentData
                      values={values}
                      errors={errors}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      disabled={isDisabled}
                    />
                  </PanelBody>
                </Panel>

                {/* Botón para agregar factura */}
                <div
                  style={{ margin: "1rem 0" }}
                  className="d-flex justify-content-end"
                >
                  <button
                    type="button"
                    onClick={() => {
                      const pagosContadoMod = !pagosSonIguales(
                        values.payments_contado,
                        values.payments_contado_original
                      );
                      const pagosCreditoMod = !pagosSonIguales(
                        values.payments_credito,
                        values.payments_credito_original
                      );

                      if (pagosContadoMod || pagosCreditoMod) {
                        Swal.fire({
                          icon: "warning",
                          title: "Pagos sin guardar",
                          text: "Existen pagos modificados que no han sido guardados.",
                        });
                        return; // No abre el modal
                      }

                      // Si no hay pagos modificados, abre el modal
                      openInvoiceModal();
                    }}
                    className="btn btn-primary"
                    disabled={
                      !tienePagosValidos(
                        values.payments_contado,
                        values.payments_credito,
                        values.total
                      )
                    }
                  >
                    <i className="fas fa-file-invoice me-2"></i>
                    Agregar factura
                  </button>
                </div>

                <AccordionPayments
                  values={values}
                  formatNumberWithCommas={formatNumberWithCommas}
                  setPaymentTypes={setPaymentTypes}
                  paymentTypes={paymentTypes}
                  isAccountant={isAccountant}
                  isAdmin={isAdmin}
                  refreshKey={refreshKey}
                  disabled={isDisabled}
                />

                <div
                  className="d-flex justify-content-between mt-4"
                  style={{ minHeight: "38px" }}
                >
                  <div
                    style={{
                      visibility:
                        canCancelVaroExpress() && values.estatus !== "Cancelado"
                          ? "visible"
                          : "hidden",
                    }}
                  >
                    <Button
                      variant="danger"
                      disabled={isCancelling}
                      onClick={async () => {
                        const result = await Swal.fire({
                          title: "¿Estás seguro?",
                          text: "Esta acción cancelará la orden.",
                          icon: "warning",
                          showCancelButton: true,
                          confirmButtonColor: "#d33",
                          cancelButtonColor: "#3085d6",
                          confirmButtonText: "Sí, cancelar",
                          cancelButtonText: "No, mantener",
                        });

                        if (!result.isConfirmed) return;

                        setIsCancelling(true);
                        try {
                          await axiosClient.put(`/order-vial/cancel/${id}`);
                          setFieldValue("estatus", "Cancelado");

                          await Swal.fire(
                            "Cancelado",
                            "La orden ha sido cancelada.",
                            "success"
                          );
                        } catch (error) {
                          console.error("Error al cancelar orden:", error);
                          const message =
                            error.response?.data?.error ||
                            "No se pudo cancelar la orden.";
                          await Swal.fire("Error", message, "error");
                        } finally {
                          setIsCancelling(false);
                        }
                      }}
                    >
                      Cancelar orden
                    </Button>
                  </div>

                  <div className="d-flex">
                    <Button
                      type="button"
                      disabled={isSubmitting || !isDisabledReimprimir}
                      className="me-2"
                      variant="success"
                      onClick={async () => {
                        // Modal ANTES del submit para que el spinner global
                        // (isSubmitting) no aparezca encima del selector.
                        const dispositivo = await askPrinterDestination();
                        if (!dispositivo) return;
                        printerDestinoRef.current = dispositivo;
                        setShouldPrintTicket(true);
                        setShouldReprintTicket(true);
                        handleSubmit();
                      }}
                    >
                      Reimprimir ticket
                    </Button>

                    <Button
                      type="button"
                      disabled={
                        isSubmitting || !isPaymentCovered(values) || isDisabled
                      }
                      className="me-2"
                      onClick={async () => {
                        const dispositivo = await askPrinterDestination();
                        if (!dispositivo) return;
                        printerDestinoRef.current = dispositivo;
                        setShouldPrintTicket(true);
                        setShouldReprintTicket(false);
                        handleSubmit();
                      }}
                    >
                      {isSubmitting
                        ? "Guardando..."
                        : "Guardar e imprimir ticket"}
                    </Button>

                    <Button
                      type="submit"
                      disabled={isSubmitting || isDisabled}
                      className="me-2"
                      onClick={() => {
                        printerDestinoRef.current = null;
                        setShouldPrintTicket(false);
                      }}
                    >
                      {isSubmitting ? "Guardando..." : "Guardar cambios"}
                    </Button>
                    <RouterLink to="/orders-vial" className="btn btn-info">
                      Regresar
                    </RouterLink>
                  </div>
                </div>
              </Col>
            </Row>
          </Form>
          );
          }}
      </Formik>

      <ModalContainer
        open={openModalInvoice}
        closeFn={closeInvoiceModal}
        title="Agregar nueva factura"
        size="xl"
      >
        <AddInvoiceForm
          closeModal={closeInvoiceModal}
          idOrden={id}
          razonSocial="VaroExpress"
          onSaved={handleInvoiceSaved} 
        />
      </ModalContainer>
    </div>
  );
}

export default EditOrderVial;
